import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {HStack, Stack, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'

import StoreDescription from './store-description'
import {useStoreContext} from '../../../../contexts/store'
import useTagManager from '../../../../hooks/use-tag-manager'

const StoreItem = ({store, setStores, setStoreDetails, setEditStore}) => {
    const intl = useIntl()
    const tagManager = useTagManager()
    const [loading, setLoading] = useState(false)
    const {store: contextStore, setStore, setRefreshBopisCart} = useStoreContext()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const isSelected = store.ID === contextStore?.ID
    const isBopisDisabled = store?.bopis === 'disabled' || store?.bopis === 'tempdisabled'

    const handleStoreDetails = async () => {
        setLoading(true)
        const token = await getTokenWhenReady()

        const result = await api.shopperStores.getStores({
            parameters: {
                ids: [store.ID]
            },
            headers: {
                Authorization: `Bearer ${token}`
            },
            keepPreviousData: true
        })
        setLoading(false)

        setStoreDetails(result?.data?.[0] || null)
        if (result?.data?.[0]?.name) {
            tagManager.storeDetailsClick(result?.data?.[0]?.name)
        }
    }

    const handleSetStore = () => {
        setStore(store)
        setRefreshBopisCart(true)
        setEditStore(false)
        setStores((stores) => {
            const sortedStores = stores
                ?.map((item) => {
                    item.currentlySelected = item.ID === store?.ID
                    return item
                })
                ?.sort((a, b) => Number(b?.currentlySelected) - Number(a?.currentlySelected))

            localStorage.setItem('savedSearchedStores', JSON.stringify(sortedStores))
            return sortedStores
        })

        tagManager.setAsMyStore(store?.name)
    }

    let ctaLabel = intl.formatMessage({
        id: 'select_store_modal.select_store',
        defaultMessage: 'Select Store'
    })
    let ctaVariant = 'pickupSelectStore'
    if (isBopisDisabled) {
        ctaLabel = intl.formatMessage({
            id: 'select_store_modal.pickup_unavailable',
            defaultMessage: 'Pickup Unavailable'
        })
        ctaVariant = 'pickupUnavailable'
    } else if (isSelected) {
        ctaLabel = intl.formatMessage({
            id: 'select_store_modal.your_pickup_store_cta',
            defaultMessage: 'Your Pickup Store'
        })
        ctaVariant = 'pickupYourStore'
    }

    return (
        <Stack
            gap="6px"
            background={isSelected ? 'gray.40' : undefined}
            paddingX="20px"
            paddingY="15px"
        >
            <StoreDescription store={store} showDistance />

            <HStack justifyContent="space-between" gap="10px">
                <Button
                    onClick={handleStoreDetails}
                    isLoading={loading}
                    variant="linkUnderline"
                    fontSize="15px"
                    fontWeight="300"
                    textUnderlineOffset="4px"
                    lineHeight="20px"
                    letterSpacing="0.5px"
                    color="black.100"
                >
                    <FormattedMessage
                        id="select_store_modal.store_details"
                        defaultMessage="Store Details"
                    />
                </Button>

                <Button
                    onClick={handleSetStore}
                    isDisabled={isBopisDisabled || isSelected}
                    variant={ctaVariant}
                >
                    {ctaLabel}
                </Button>
            </HStack>
        </Stack>
    )
}

StoreItem.propTypes = {
    store: PropTypes.shape({
        ID: PropTypes.string,
        name: PropTypes.string,
        distance: PropTypes.string,
        calloutMessage: PropTypes.string,
        phone: PropTypes.string,
        address1: PropTypes.string,
        city: PropTypes.string,
        stateCode: PropTypes.string,
        zip: PropTypes.string,
        bopis: PropTypes.string
    }).isRequired,
    setStores: PropTypes.func.isRequired,
    setStoreDetails: PropTypes.func.isRequired,
    setEditStore: PropTypes.func.isRequired
}

export default StoreItem
